import * as React from "react";
import ImageView from "../../../components/SmartImage";
import { DragPoints } from "../../../utils/dragAndDrop";
import { CardBannerContainer, SingleSlidingWrap } from "./styles";
import { SingleSlidingProps } from "./utils";
import { isNotBlankArray } from "utils/array";
import { TBannerItem } from "../../../services/banner";
import { px2rem } from "../../../utils/css";
import { LinkTargetType } from "../../../services/playground";
import { transformAParam } from "utils/browser";

interface SingleSlidingState {
	cIndex: number;
	animationDuration: number;
	dragPoints: DragPoints;
	wrapperOffsetLeft: number;
	startTime: number;
	containerWidth?: number;
}

class SingleSliding extends React.Component<SingleSlidingProps, SingleSlidingState> {
	static defaultProps = {
		animationDuration: 200,
		swipeSensitive: 1,
		speedSensitive: 320,
	};

	state: SingleSlidingState = {
		cIndex: 2,
		animationDuration: 0,
		dragPoints: {},
		wrapperOffsetLeft: 0,
		startTime: 0,
	};

	private scrollItemsWrapper: HTMLDivElement;
	private container: HTMLDivElement;
	timeoutId: NodeJS.Timeout;
	windowScrollTimeoutId: any = 0;
	isStop = false;

	getStyle() {
		const { cIndex, animationDuration, dragPoints } = this.state;
		const imgWidth = this.props.cardSize.cardWidth;
		const style: React.CSSProperties = {};
		const offset = imgWidth === 280 ? 36 : 86;
		const wrapperOffsetLeft = -1 * cIndex * (imgWidth + 12) + offset;
		if (this.isDraging()) {
			const { endPoint, startPoint } = dragPoints as any;
			const distance = typeof endPoint === "undefined" ? 0 : startPoint.x - endPoint.x;
			style.transform = `translate3d(${(wrapperOffsetLeft - distance) / 16}rem,0,0)`;
		} else {
			style.transform =
				cIndex === 0
					? "translate3d(0,0,0)"
					: `translate3d(${wrapperOffsetLeft / 16}rem,0,0)`;
			style.transitionDuration = `${animationDuration}ms`;
		}
		style.WebkitTransform = style.transform;
		style.transitionTimingFunction = "ease-out";
		return style;
	}
	getScrollWrapWidth() {
		const imgWidth = this.props.cardSize.cardWidth;
		return `calc(${this.scrollItemsWrapper?.childElementCount || 3}*${imgWidth + 12}px)`;
	}

	onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		const { animationDuration } = this.state;

		if (!this.isDraging() && animationDuration === 0) {
			this.stopAutoScroll();

			const touch = e.touches[0];
			const dragPoints = {
				startPoint: {
					x: touch.clientX,
					y: touch.clientY,
				},
			};
			this.setState({
				dragPoints,
				wrapperOffsetLeft: this.scrollItemsWrapper.getBoundingClientRect().left,
				startTime: new Date().getTime(),
			});
		}
	};

	onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (this.isDraging()) {
			const touch = e.touches[0];
			const endPoint = {
				x: touch.clientX,
				y: touch.clientY,
			};
			if (
				Math.abs(this.getDistanceY(this.state.dragPoints)) >
				Math.abs(this.getDistanceX(this.state.dragPoints))
			) {
				this.setState({ dragPoints: {} });
				this.autoScroll();
			} else {
				const dragPoints = {
					...this.state.dragPoints,
					endPoint,
				};
				this.setState({ dragPoints });
				e.stopPropagation();
			}
		}
	};

	onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
		const { dragPoints, containerWidth, startTime, cIndex } = this.state;

		const distance = Math.abs(this.getDistanceX(dragPoints));
		if (this.isDraging() && distance) {
			let updateState = {
				dragPoints: {},
				cIndex,
			} as SingleSlidingState;
			if (typeof (dragPoints as DragPoints).endPoint !== "undefined") {
				const { animationDuration = 0, swipeSensitive, speedSensitive } = this.props;
				const endTime = new Date().getTime();
				const slideSpeed = (distance * 1000) / (endTime - startTime);
				if (
					distance >
						Math.ceil(((containerWidth as number) * (swipeSensitive as number)) / 2) ||
					slideSpeed > (speedSensitive as number)
				) {
					const scrollNumber = Math.ceil(distance / (containerWidth as number));
					if (this.getDistanceX(dragPoints) < 0) {
						updateState.cIndex = cIndex - scrollNumber;
					} else {
						updateState.cIndex = cIndex + scrollNumber;
					}
				}
				updateState.cIndex = Math.max(0, updateState.cIndex);
				updateState.cIndex = Math.min(this.getNodeCount() - 1, updateState.cIndex);
				if (updateState.cIndex <= 1) {
					updateState.cIndex = 1;
				}

				const duration = Math.ceil(
					(distance / (containerWidth as number)) * animationDuration,
				);

				const nextAnimationDuration =
					duration > animationDuration
						? (animationDuration as number) - (duration % animationDuration)
						: Math.max(50, duration);
				updateState = {
					...updateState,
					animationDuration: nextAnimationDuration,
				};
			}

			this.setState(updateState);
			this.autoScroll();
		}
		e.stopPropagation();
	};

	onTransitionEnd = () => {
		const { cardBanners } = this.props;
		const { cIndex } = this.state;
		const updateState = {} as SingleSlidingState;

		if (cIndex > cardBanners.length + 1) {
			updateState.cIndex = 2;
		} else if (cIndex === 1) {
			updateState.cIndex = cardBanners.length + 1;
		}
		updateState.animationDuration = 0;
		this.setState(updateState as any);
	};

	getNodeCount() {
		const { cardBanners } = this.props;
		return cardBanners.length + 4;
	}

	getDistanceX = (dragPoints: DragPoints): number => {
		const { endPoint, startPoint } = dragPoints;
		if (typeof endPoint !== "undefined" && typeof startPoint !== "undefined") {
			return startPoint.x - endPoint.x;
		}
		return 0;
	};
	getDistanceY = (dragPoints: DragPoints): number => {
		const { endPoint, startPoint } = dragPoints;
		if (typeof endPoint !== "undefined" && typeof startPoint !== "undefined") {
			return startPoint.y - endPoint.y;
		}
		return 0;
	};

	isDraging(): boolean {
		return !!this.state.dragPoints.endPoint || !!this.state.dragPoints.startPoint;
	}

	renderCardBanner = (item: TBannerItem, index: number) => {
		const { cardSize, placeholderBg, aTarget = LinkTargetType.Blank, isInApp } = this.props;
		const height = cardSize.cardHeight;
		const aProps = transformAParam({
			isInApp: !!isInApp,
			link: item.link,
			aTarget,
		});
		return (
			<a
				// {...(isNotBlankString(item.link) ? { href: item.link, target: aTarget } : {})}
				{...aProps}
				key={index}
			>
				<ImageView
					src={item.img}
					aliSize={2}
					qnParams={{ w: +(item.width || 750) }}
					isLazyLoad={false}
					online={this.props.online}
					placeholder={placeholderBg}
					fallbackSrc={placeholderBg}
					style={{
						height: this.state.cIndex === index ? `${height}` : `calc(${height} * 0.9)`,
						opacity: this.state.cIndex === index ? 1 : 0.6,
					}}
				/>
			</a>
		);
	};

	render() {
		const { positionId, cardBanners: elements, cardSize } = this.props;
		if (!isNotBlankArray(elements)) {
			return null;
		}
		const cardWidth = px2rem(cardSize.cardWidth);

		return (
			<CardBannerContainer
				id={positionId}
				ref={this.bindContainerRef}
				onTouchStart={this.onTouchStart}
				onTouchMove={this.onTouchMove}
				onTouchEnd={this.onTouchEnd}
				onTouchCancel={this.onTouchEnd}
				style={{ minHeight: cardSize.cardHeight ? `calc(${cardSize.cardHeight})` : "auto" }}
			>
				<section>
					<SingleSlidingWrap
						cardWidth={cardWidth}
						style={this.getStyle()}
						ref={this.bindScrollItemsWrapperRef}
						onTransitionEnd={this.onTransitionEnd}
					>
						{(elements || [])
							.slice(-2)
							.map((item, index) => this.renderCardBanner(item, index))}
						{(elements || []).map((item, index) =>
							this.renderCardBanner(item, index + 2),
						)}
						{(elements || [])
							.slice(0, 2)
							.map((item, index) =>
								this.renderCardBanner(item, index + 2 + elements.length),
							)}
					</SingleSlidingWrap>
				</section>
			</CardBannerContainer>
		);
	}

	autoScroll() {
		clearTimeout(this.timeoutId);
		this.timeoutId = setTimeout(() => {
			if (!this.isStop && document.visibilityState === "visible") {
				const { cIndex } = this.state;
				this.scrollByIndex(cIndex + 1);
			}
			this.autoScroll();
		}, 4000);
	}

	scrollByIndex(index: number) {
		this.setState({
			cIndex: index,
			animationDuration: this.props.animationDuration as number,
		});
	}

	stopAutoScroll() {
		clearTimeout(this.timeoutId);
	}

	bindContainerRef = (me: HTMLDivElement) => {
		this.container = me;
	};

	bindScrollItemsWrapperRef = (me: HTMLDivElement) => {
		this.scrollItemsWrapper = me;
	};

	onResize = () => {
		this.setState({ containerWidth: this.container.getBoundingClientRect().width });
	};

	onWindowScroll = () => {
		clearTimeout(this.windowScrollTimeoutId);
		this.windowScrollTimeoutId = setTimeout(() => {
			if (this.isContainerInCurrentScreen()) {
				this.isStop = false;
			} else {
				this.isStop = true;
			}
		}, 80);
	};

	componentDidMount() {
		this.setState({
			containerWidth: this.container.getBoundingClientRect().width,
		});
		this.autoScroll();
		window.addEventListener("scroll", this.onWindowScroll);
		window.addEventListener("resize", this.onResize);
	}

	componentWillUnmount() {
		this.stopAutoScroll();
		clearTimeout(this.windowScrollTimeoutId);
		window.removeEventListener("scroll", this.onWindowScroll);
		window.removeEventListener("resize", this.onResize);
	}

	isContainerInCurrentScreen() {
		return this.container.getBoundingClientRect().bottom >= 0;
	}
}

export default SingleSliding;
